import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { ClipboardText, Plus } from "@phosphor-icons/react";

const STATUS_MAP = {
  pending: { label: "Beklemede", color: "bg-yellow-200" },
  documents_received: { label: "Evraklar Alındı", color: "bg-blue-200" },
  paid: { label: "Ödendi", color: "bg-green-200" },
  active: { label: "Aktif Kursiyer", color: "bg-brand-primary text-white" },
  completed: { label: "Tamamlandı", color: "bg-brand-primarySubtle" },
  rejected: { label: "Reddedildi", color: "bg-red-200" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.get("/registrations/mine").then(r => setRegs(r.data)); }, []);
  if (!user) return null;

  return (
    <>
      <SEO title="Panelim | Estem"/>
      <section className="px-4 md:px-8 py-12 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="nb-badge mb-2">Panelim</div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Merhaba, {user.full_name.split(" ")[0]}</h1>
          </div>
          <Link to="/basla" className="nb-btn" data-testid="dash-new-reg"><Plus weight="bold"/> Yeni Kayıt</Link>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <div className="nb-card p-6"><div className="nb-label">Toplam Başvuru</div><div className="font-heading font-bold text-4xl mt-1">{regs.length}</div></div>
          <div className="nb-card p-6 bg-brand-primarySubtle"><div className="nb-label">Aktif</div><div className="font-heading font-bold text-4xl mt-1">{regs.filter(r=>r.status==="active").length}</div></div>
          <div className="nb-card p-6 !bg-brand-primary text-white"><div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-white/60">Beklemede</div><div className="font-heading font-bold text-4xl mt-1">{regs.filter(r=>r.status==="pending").length}</div></div>
        </div>

        <h2 className="font-heading font-bold text-2xl uppercase mt-12 mb-4">Başvurularım</h2>
        {regs.length === 0 ? (
          <div className="nb-card p-12 text-center">
            <ClipboardText size={48} weight="duotone" className="mx-auto mb-4 text-brand-muted"/>
            <p className="text-brand-muted">Henüz başvurun yok.</p>
            <Link to="/basla" className="nb-btn mt-4 inline-flex" data-testid="dash-empty-cta">İlk başvurunu yap</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {regs.map(r => {
              const s = STATUS_MAP[r.status] || STATUS_MAP.pending;
              const docCount = (r.documents || []).length;
              const totalDocs = 3;
              const checklist = [
                { l: "Hesap", ok: true },
                { l: "Bilgi", ok: true },
                { l: "Sınıf", ok: !!r.course_class },
                { l: "Ödeme Planı", ok: !!r.payment?.plan },
                { l: "Belgeler", ok: docCount >= totalDocs },
                { l: "Ödeme Onayı", ok: r.payment?.status === "received" || r.status === "paid" || r.status === "active" },
              ];
              const doneCnt = checklist.filter(c => c.ok).length;
              const pct = Math.round((doneCnt / checklist.length) * 100);
              return (
                <div key={r.id} className="nb-card p-6" data-testid={`reg-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="nb-label">Kayıt #{r.id.slice(0,8).toUpperCase()}</div>
                      <div className="font-heading font-bold text-2xl mt-1">{r.course_class} Sınıfı</div>
                      <div className="text-sm text-brand-muted mt-1">{new Date(r.created_at).toLocaleDateString("tr-TR")}</div>
                    </div>
                    <span className={`px-4 py-2 rounded-full border border-stone-200 font-bold text-sm ${s.color}`}>{s.label}</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                      <span>Süreç tamamlanma</span>
                      <span className="font-bold">%{pct}</span>
                    </div>
                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-primary transition-all" style={{width: `${pct}%`}}/>
                    </div>
                  </div>
                  {/* Checklist */}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {checklist.map((c, i) => (
                      <div key={i} className={`px-2 py-1 rounded-md ${c.ok ? "bg-brand-success/10 text-brand-success" : "bg-stone-100 text-stone-400"}`}>
                        {c.ok ? "✓" : "○"} {c.l}
                      </div>
                    ))}
                  </div>
                  {pct < 100 && (
                    <Link to={`/basvurum/${r.id}`} className="nb-btn mt-4 inline-flex !py-2 !text-xs" data-testid={`reg-continue-${r.id}`}>
                      Başvuruya Devam Et
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
